import { useMutation } from '@apollo/client';
import { useForm, UseFormReturnType } from '@mantine/form';
import { useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { setNewPassword } from '@/api';
import { resolveGqlErrors } from '@/helpers';
import { getPasswordRequirements } from './helpers';

export type FormValues = {
  password: string;
  passwordConfirmation: string;
};

type UseForgotPasswordReset = () => {
  onSubmit: () => void;
  form: UseFormReturnType<FormValues>;
  loading: boolean;
  error: string;
  success: boolean;
  token: string | null;
  passwordRequirements: Array<{ re: RegExp; label: string; meets: boolean }>;
};

const useForgotPasswordReset: UseForgotPasswordReset = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('reset_password_token');

  const requirements = getPasswordRequirements();

  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resetPassword, { loading }] = useMutation(setNewPassword);

  const form = useForm<FormValues>({
    initialValues: {
      password: '',
      passwordConfirmation: '',
    },
    validate: {
      password: (value) => {
        const allRequirementsMet = requirements.every((requirement) => requirement.re.test(value));
        if (!allRequirementsMet) {
          return 'Password does not meet all requirements';
        }
        return null;
      },
      passwordConfirmation: (value, values) => {
        if (value !== values.password) {
          return 'Passwords need to match';
        }
        return null;
      },
    },
  });

  const onSubmit = form.onSubmit(async ({ password, passwordConfirmation }) => {
    if (!token) {
      setError('Invalid or missing reset token. Please request a new password reset link.');
      return;
    }

    setError('');

    try {
      const { data } = await resetPassword({
        variables: {
          password,
          passwordConfirmation,
          token,
        },
      });

      const errorMessage = resolveGqlErrors(data?.setNewPassword?.error);

      if (errorMessage) {
        setError(errorMessage);
        return;
      }

      setSuccess(true);
    } catch (err) {
      const errorMessage = resolveGqlErrors(err);
      setError(errorMessage || 'An unexpected error occurred. Please try again.');
    }
  });

  const passwordRequirements = useMemo(
    () =>
      requirements.map((requirement) => ({
        ...requirement,
        meets: requirement.re.test(form.values.password),
      })),
    [requirements, form.values.password]
  );

  return {
    onSubmit,
    form,
    loading,
    error,
    success,
    token,
    passwordRequirements,
  };
};

export default useForgotPasswordReset;
